import { AtwLogo } from "@/components/AtwLogo";
import { cn } from "@/lib/utils";

export type ConnectionBrandId =
  | "gmail"
  | "google"
  | "google-calendar"
  | "outlook"
  | "yahoo"
  | "zoho"
  | "zoho-mail"
  | "cliq"
  | "zoho-cliq"
  | "atw"
  | "atw-mail"
  | "slack"
  | "zoom"
  | string;

function normalizeBrandId(id: string): string {
  return id.trim().toLowerCase();
}

function GoogleMark() {
  return (
    <svg viewBox="0 0 24 24" role="img" aria-hidden>
      <path
        fill="#4285F4"
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
      />
      <path
        fill="#34A853"
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
      />
      <path
        fill="#FBBC05"
        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
      />
      <path
        fill="#EA4335"
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
      />
    </svg>
  );
}

function OutlookMark() {
  return (
    <svg viewBox="0 0 24 24" role="img" aria-hidden>
      <rect x="3" y="3" width="8.5" height="8.5" rx="1.2" fill="#F25022" />
      <rect x="12.5" y="3" width="8.5" height="8.5" rx="1.2" fill="#7FBA00" />
      <rect x="3" y="12.5" width="8.5" height="8.5" rx="1.2" fill="#00A4EF" />
      <rect x="12.5" y="12.5" width="8.5" height="8.5" rx="1.2" fill="#FFB900" />
    </svg>
  );
}

function YahooMark() {
  return (
    <svg viewBox="0 0 24 24" role="img" aria-hidden>
      <rect width="24" height="24" rx="6" fill="#6001D2" />
      <path
        fill="#fff"
        d="M7.2 6.2h2.3l2.5 5.2 2.5-5.2h2.3l-3.8 7.4V17.8h-2.1v-4.2L7.2 6.2Zm9.8 9.2a1.15 1.15 0 1 1 0 2.3 1.15 1.15 0 0 1 0-2.3Z"
      />
    </svg>
  );
}

function ZohoMark() {
  return (
    <svg viewBox="0 0 24 24" role="img" aria-hidden>
      <rect width="24" height="24" rx="6" fill="#E42527" />
      <path fill="#fff" d="M6.8 7.2h10.4v2.1l-6.2 7.5h6.5V19H6.5v-2.1l6.3-7.5H6.8V7.2Z" />
    </svg>
  );
}

function SlackMark() {
  return (
    <svg viewBox="0 0 24 24" role="img" aria-hidden>
      <path
        fill="#E01E5A"
        d="M9.2 14.4a1.6 1.6 0 1 1-1.6-1.6h1.6v1.6Zm.8 0a1.6 1.6 0 1 1 3.2 0v4a1.6 1.6 0 1 1-3.2 0v-4Z"
      />
      <path
        fill="#36C5F0"
        d="M9.6 9.2a1.6 1.6 0 1 1 1.6-1.6v1.6H9.6Zm0 .8a1.6 1.6 0 1 1 0 3.2h-4a1.6 1.6 0 1 1 0-3.2h4Z"
      />
      <path
        fill="#2EB67D"
        d="M14.8 9.6a1.6 1.6 0 1 1 1.6 1.6h-1.6V9.6Zm-.8 0a1.6 1.6 0 1 1-3.2 0v-4a1.6 1.6 0 1 1 3.2 0v4Z"
      />
      <path
        fill="#ECB22E"
        d="M14.4 14.8a1.6 1.6 0 1 1-1.6 1.6v-1.6h1.6Zm0-.8a1.6 1.6 0 1 1 0-3.2h4a1.6 1.6 0 1 1 0 3.2h-4Z"
      />
    </svg>
  );
}

function ZoomMark() {
  return (
    <svg viewBox="0 0 24 24" role="img" aria-hidden>
      <rect width="24" height="24" rx="6" fill="#0B5CFF" />
      <path
        fill="#fff"
        d="M5.5 9.2h7.2c.9 0 1.6.7 1.6 1.6v4.4c0 .9-.7 1.6-1.6 1.6H5.5c-.9 0-1.6-.7-1.6-1.6v-4.4c0-.9.7-1.6 1.6-1.6Zm10.2 1.3 3.4-2.1c.5-.3 1.1.1 1.1.7v6.2c0 .6-.6 1-1.1.7l-3.4-2.1v-3.4Z"
      />
    </svg>
  );
}

function TeamsMark() {
  return (
    <svg viewBox="0 0 24 24" role="img" aria-hidden>
      <rect width="24" height="24" rx="6" fill="#464EB8" />
      <path
        fill="#fff"
        d="M6.5 8.5h5.2v2.2H8.7v5.1H6.5V8.5Zm10.3 0c1.4 0 2.5 1.1 2.5 2.5v3.4c0 1.4-1.1 2.5-2.5 2.5H13v-8.4h3.8Zm-.2 2.2h-1.4v3.9h1.4c.5 0 .9-.4.9-.9v-2.1c0-.5-.4-.9-.9-.9Z"
      />
    </svg>
  );
}

function GenericMark() {
  return (
    <svg viewBox="0 0 24 24" role="img" aria-hidden>
      <rect width="24" height="24" rx="6" fill="#5d6f7c" />
      <path
        fill="#fff"
        d="M8.2 10.2a2.3 2.3 0 0 1 3.2 0l.4.4 1.2-1.2-.4-.4a4 4 0 0 0-5.6 5.6l.4.4 1.2-1.2-.4-.4a2.3 2.3 0 0 1 0-3.2Zm7.6 3.6a2.3 2.3 0 0 1-3.2 0l-.4-.4-1.2 1.2.4.4a4 4 0 0 0 5.6-5.6l-.4-.4-1.2 1.2.4.4a2.3 2.3 0 0 1 0 3.2ZM10.2 12.6l1.4-1.4 2.2 2.2-1.4 1.4-2.2-2.2Z"
      />
    </svg>
  );
}

function BrandGlyph({ id }: { id: string }) {
  if (id === "gmail" || id === "google" || id === "google-calendar") return <GoogleMark />;
  if (id === "outlook") return <OutlookMark />;
  if (id === "yahoo") return <YahooMark />;
  if (id === "zoho" || id === "zoho-mail" || id === "cliq" || id === "zoho-cliq") {
    return <ZohoMark />;
  }
  if (id === "atw" || id === "atw-mail") {
    /* Light surfaces: white-field squircle lockup (logo.png) — not white-on-navy. */
    return <AtwLogo size={22} label="ATW" className="brand-icon-atw-img" />;
  }
  if (id === "slack") return <SlackMark />;
  if (id === "zoom") return <ZoomMark />;
  if (id === "teams" || id === "microsoft-teams") return <TeamsMark />;
  return <GenericMark />;
}

type ConnectionBrandIconProps = {
  id: ConnectionBrandId;
  label?: string;
  size?: number;
  className?: string;
};

/** Recognizable brand marks for Connections cards (not letter chips). */
export function ConnectionBrandIcon({
  id,
  label,
  size = 28,
  className,
}: ConnectionBrandIconProps) {
  const normalized = normalizeBrandId(String(id || "generic"));
  const title = label || normalized;
  return (
    <span
      className={cn("brand-icon", `brand-icon-${normalized}`, className)}
      title={title}
      aria-hidden
      style={{ width: size, height: size }}
    >
      <BrandGlyph id={normalized} />
    </span>
  );
}
